import { Component, Input } from '@angular/core';
import { LoadingService } from 'ng-devui';
import { catchError, throwError, zip } from 'rxjs';
import { DialogConfig } from 'src/app/modules/shared/components/alert-dialog/dialog-config';
import { FilePreviewTypeListMap } from 'src/app/modules/shared/components/file-preview-entry/file-preview-entry.component';
import { ArchiveFile } from 'src/app/modules/shared/models/archive/archive-file.model';
import { FileType } from 'src/app/modules/shared/models/form/file-type.enum';
import { FormHeaderSearch } from 'src/app/modules/shared/models/form/form-header-search.enum';
import { WorkReport } from 'src/app/modules/shared/models/form/work-report.model';
import { ProjectSection } from 'src/app/modules/shared/models/project/project-section.model';
import { OrganizationCategory } from 'src/app/modules/shared/models/staff/organization-category.enum';
import { OrganizationListRequest } from 'src/app/modules/shared/models/staff/organization-list-request.model';
import {
  OrganizationType,
  OrganizationTypeShortMap,
} from 'src/app/modules/shared/models/staff/organization-type.enum';
import { Organization } from 'src/app/modules/shared/models/staff/organization.model';
import { NzTreeNode } from 'src/app/modules/shared/models/tree-node.model';
import { GlobalService } from 'src/app/modules/shared/providers/global.service';
import { ArchiveFileService } from 'src/app/modules/shared/services/archive-file.service';
import { ProjectSectionService } from 'src/app/modules/shared/services/project-section.service';
import { StaffOrganizationService } from 'src/app/modules/shared/services/staff-organization.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-dialog-archive-upload-fill',
  templateUrl: './dialog-archive-upload-fill.component.html',
  styleUrls: ['./dialog-archive-upload-fill.component.scss'],
})
export class DialogArchiveUploadFillComponent {
  @Input()
  data!: DialogConfig<ArchiveFile>;

  editing: { [name: string]: boolean } = {};

  record: ArchiveFile = new ArchiveFile();

  FilePreviewTypeListMap = FilePreviewTypeListMap;
  FileType = FileType;
  FormHeaderSearch = FormHeaderSearch;

  currentFileType = FileType.PDF;

  environment = environment;

  height = 0;
  width = 0;
  // index = 0;

  pdfZoom = 1;
  pdfPages = 1;
  pdfTotal = 0;

  loading: any;

  orgTreeNode: NzTreeNode[] = [];

  orgCascaderValue: any[] = [];

  sections: ProjectSection[] = [];

  get withToken() {
    return '?token=' + this.globalService.token;
  }

  constructor(
    private globalService: GlobalService,
    private archiveFileService: ArchiveFileService,
    private projectSectionService: ProjectSectionService,
    private staffOrganizationService: StaffOrganizationService,
    private loadingService: LoadingService,
  ) {}

  private prepare() {
    zip(
      this.projectSectionService.findAll({
        projectId: this.globalService.projectId,
      }),
      this.staffOrganizationService.findAll<OrganizationListRequest>({
        projectId: this.globalService.projectId,
      }),
    ).subscribe(([sectionList, orgList]) => {
      this.sections = sectionList;
      const institutions: Organization[] = orgList.filter(
        (org) => org.category === OrganizationCategory.INSTITUTION,
      );
      const nodesMap: { [key: string]: NzTreeNode[] } = {};
      for (const item of sectionList ?? []) {
        if (!nodesMap[item.type!]) {
          nodesMap[item.type!] = [];
        }
        const sectionNode: NzTreeNode = {
          label: item.name!,
          value: item.id!,
          children: [],
        };
        for (const org of item.projectSectionOrganizations ?? []) {
          if (org.organizationType !== item.type) {
            continue;
          }
          sectionNode.children!.push({
            label: org.organizationName,
            value: org.organizationName,
            isLeaf: true,
          });
        }
        for (const institution of institutions) {
          let include = false;
          for (const org of item.projectSectionOrganizations ?? []) {
            if (org.sectionId === institution.sectionId) {
              include = true;
              break;
            }
          }
          if (include) {
            sectionNode.children!.push({
              label: institution.name,
              value: institution.name,
              isLeaf: true,
            });
          }
        }
        if (sectionNode.children!.length > 0) {
          nodesMap[item.type!].push(sectionNode);
        }
      }
      const nodes: NzTreeNode[] = [];
      for (const key in nodesMap) {
        nodes.push({
          label: OrganizationTypeShortMap[key as OrganizationType],
          value: key,
          children: nodesMap[key],
        });
      }
      this.orgTreeNode = nodes;
    });
  }

  ngOnInit() {
    this.record = this.handleDataBeforeInit(this.data.data!);
    this.prepare();
  }

  ngAfterViewInit(): void {
    window.addEventListener('resize', () => {
      this.resize();
    });
    setTimeout(() => {
      this.resize();
    }, 100);
  }

  resize() {
    this.height = document.body.clientHeight;
    this.width = document.body.clientWidth - 610;
  }

  onPageLoad(event: any) {
    console.log(event);
    this.loading.loadingInstance.close();
    this.pdfTotal = event.source.pdfDocument._pdfInfo.numPages;
    this.resize();
  }

  prePage() {
    if (this.pdfPages > 1) {
      this.pdfPages--;
    }
  }

  nextPage() {
    if (this.pdfPages < this.pdfTotal) {
      this.pdfPages++;
    }
  }

  originSize() {
    this.pdfZoom = 1;
  }

  zoomIn() {
    this.pdfZoom += 0.1;
    if (this.pdfZoom > 2) {
      this.pdfZoom = 2;
    }
  }

  zoomOut() {
    this.pdfZoom -= 0.1;
    if (this.pdfZoom < 0.5) {
      this.pdfZoom = 0.5;
    }
  }

  onBlur() {
    this.editing = {};
  }

  private handleDataBeforeInit(response: WorkReport): WorkReport {
    const data = JSON.parse(JSON.stringify(response)) as WorkReport;
    return data;
  }

  isNaN(content: number) {
    return window.isNaN(content);
  }

  cancel() {
    this.data.onCancel();
  }

  confirm() {
    this.submit();
  }

  private handleDataBeforeConfirm(response: ArchiveFile): ArchiveFile {
    const data = JSON.parse(JSON.stringify(response)) as ArchiveFile;
    if (data.finishDateDate) {
      data.finishDate = new Date(data.finishDateDate).getTime();
    }
    if (data.submitDateDate) {
      data.submitDate = new Date(data.submitDateDate).getTime();
    }
    if (this.orgCascaderValue.length > 0) {
      data.publishOrgName = this.orgCascaderValue[2];
      data.publishSectionId = this.orgCascaderValue[1];
      if (this.orgCascaderValue[0] === OrganizationType.CONSTRUCTION) {
        data.sectionIds = [this.orgCascaderValue[1]];
      }
      if (this.orgCascaderValue[0] === OrganizationType.SUPERVISE) {
        data.superviseSectionIds = [this.orgCascaderValue[1]];
      }
    }
    if (!data.formSn) {
      data.formSn = '';
    }
    if (!data.formCode) {
      data.formCode = '';
    }
    if (!data.formName) {
      data.formName = '';
    }
    return data;
  }

  get canConfirm() {
    if (this.orgCascaderValue.length === 0) {
      return false;
    }
    if (
      !this.record.fileTitle ||
      !this.record.submitDateDate ||
      !this.record.finishDateDate
    ) {
      return false;
    }
    return true;
  }

  private submit() {
    const loading = this.loadingService.open();
    this.archiveFileService
      .create(this.handleDataBeforeConfirm(this.record))
      .pipe(
        catchError((err) => {
          loading.loadingInstance.close();
          return throwError(err);
        }),
      )
      .subscribe((res) => {
        loading.loadingInstance.close();
        if (res) {
          this.data.onConfirm!(res);
        }
      });
  }
}
